/**
 * PopupMenu
 * 
 * @Author: Focci
 * @Date: 2023-08-11 08:50:14
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-11 08:50:14
 */

'use client'

// import PropTypes from 'prop-types'
import AddIcon from '@mui/icons-material/Add'
import useMutationModule from '@hook/useMutationModule'
import useApp from '@hook/useApp'
import clsx from 'clsx'
import { ButtonBase, Popover } from '@comp/material'
import { debounce } from 'lodash-es'
import { useCallback, useState } from 'react'
import RoundTrigger from './RoundTrigger'

const intoView = debounce((el) => el.scrollIntoView({ behavior: 'smooth' }), 100)

export function FloatingMenuPanel({
  wrapperClassName = 'flex flex-col border shadow-simple py-1 rounded-md',
  buttonClassName = 'w-full',
  itemClassName = 'justify-start px-2 py-1 hover:bg-cf0',
  activedItemClassName,
  onChange = () => {}
}) {
  const { options, value } = useMutationModule()
  const handleClick = useCallback((option) => {
    intoView(option.el)
    onChange(option)
  }, [onChange])
  
  if(options.length === 0) {
    return null
  }

  return (
    <div className={clsx(['bg-white', wrapperClassName])}>
      {options.map((option) => (
        <ButtonBase
          key={option.value}
          className={buttonClassName}
          onClick={() => handleClick(option)}
        >
          <div 
            className={clsx([
              'flex w-full max-w-24 text-left line-clamp-2',
              value === option.value 
                ? (activedItemClassName || itemClassName)
                : itemClassName
            ])}
          >
            {option.label}
          </div>
        </ButtonBase>
      ))}
    </div>
  )
}

export default function PopupMenu({
  hideWidthThan = 1600, // 如果宽度大于 hideWidthThan 则隐藏
}) {
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const { options } = useMutationModule()
  const { clientWidth } = useApp()

  const handleClick = useCallback((e) => {
    setAnchorEl(e.currentTarget)
    setOpen(!open)
  }, [open])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
    setOpen(!open)
  }, [open])

  if(options.length === 0 || clientWidth > hideWidthThan) {
    return null
  }

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <div>
          <div className="w-[90vw] md:w-80">
            <FloatingMenuPanel 
              wrapperClassName="grid grid-cols-2 md:grid-cols-3 gap-1 px-3 pt-3 gap-3"
              itemClassName="border rounded-sm p-1 justify-center hover:bg-cf0"
              activedItemClassName="border border-primary text-primary rounded-sm p-1 justify-center"
              onChange={handleClose}
            />
          </div>
          <div className="p-2 text-right">
            <ButtonBase onClick={handleClose}>
              <div className="inline-flex rotate-45 text-primary p-1">
                <AddIcon sx={{ fontSize: '1.5rem' }} />
              </div>
            </ButtonBase>
          </div>
        </div>
      </Popover>
      <RoundTrigger onClick={handleClick}>
        <div className={clsx(['text-primary transition-transform', open && 'rotate-45'])}>
          <AddIcon sx={{ fontSize: '1.3rem' }} />
        </div>
      </RoundTrigger>
    </>
  )
}

// PopupMenu.propTypes = {
//   children: PropTypes.node,
// }
